type OpenerModule = typeof import('pack-opener-js/react');
export type OpenerComponent = OpenerModule['default'];

let pending: Promise<OpenerComponent> | null = null;

/**
 * Pixi under this document's CSP (public/_headers). The renderer compiles
 * its uniform-sync code with `new Function`, which `script-src` forbids; the
 * unsafe-eval polyfill swaps that for plain code and has to be installed
 * before the first renderer exists, so it rides the same import as the
 * library. Texture decoding would spawn workers from `blob:` scripts, which
 * `worker-src` (falling back to `script-src 'self'`) forbids as well —
 * decoding on the main thread instead costs nothing for the two or three
 * textures a scene loads.
 */
async function preparePixi(): Promise<void> {
  const [, { loadTextures }] = await Promise.all([import('pixi.js/unsafe-eval'), import('pixi.js')]);
  const { config } = loadTextures;
  if (config) config.preferWorkers = false;
}

/**
 * The library — and Pixi with it — lives in its own chunk so the slot lane
 * never pays for it. One import, shared by the preload and every stage; a
 * failed import is forgotten so the next stage gets to try again.
 */
export function loadOpener(): Promise<OpenerComponent> {
  if (!pending) {
    pending = Promise.all([preparePixi(), import('pack-opener-js/react')])
      .then(([, module]) => module.default)
      .catch((error: unknown) => {
        pending = null;
        throw error;
      });
  }
  return pending;
}

/**
 * Warm the chunk the moment the operator's mechanic is known — at boot, long
 * before a pack is opened — so the scene can mount the instant the artwork
 * is in. The library fetches its own sounds (a chunk of theirs) once a scene
 * with sound on is created. A failure here is nobody's concern: the stage
 * reports its own.
 */
export function preloadOpener(): void {
  loadOpener().catch(() => {});
}
